import { useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { supabase } from '../../supabase';
import {
  esEmailValido,
  normalizarEmail,
  obtenerMensajeErrorAuth,
} from '../../utils/authMessages';

import {
  Alert,
  Button,
  Card,
  Col,
  Container,
  FloatingLabel,
  Form,
  Modal,
  Row,
  Spinner,
} from 'react-bootstrap';

export default function LoginPage() {
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState({ shown: false, message: '', variant: '' });
  const [showForgotPassword, setShowForgotPassword] = useState(false);
  const [resetEmail, setResetEmail] = useState('');
  const [resetResult, setResetResult] = useState(null);
  const [resetLoading, setResetLoading] = useState(false);
  const [resetSent, setResetSent] = useState(false);
  const [mostrarPass, setMostrarPass] = useState(false);

  const navigate = useNavigate();

  const handleLogin = async (event) => {
    event.preventDefault();

    if (loading) return;

    const form = event.currentTarget;
    const email = normalizarEmail(form.elements.email?.value);
    const password = form.elements.password?.value || '';

    setResult({ shown: false, message: '', variant: '' });
    setLoading(true);

    try {
      const { data, error } = await supabase.auth.signInWithPassword({
        email,
        password,
      });

      if (error) throw error;

      if (!data?.session || !data?.user) {
        throw new Error('missing-session-after-login');
      }

      navigate('/servicios', { replace: true });
    } catch (error) {
      setResult({
        message: obtenerMensajeErrorAuth(
          error,
          'No fue posible iniciar sesión. Verificá tus datos e intentá nuevamente.'
        ),
        variant: 'danger',
        shown: true,
      });
    } finally {
      setLoading(false);
    }
  };

  const handleForgotPassword = async (event) => {
    event?.preventDefault();

    if (resetLoading || resetSent) return;

    const email = normalizarEmail(resetEmail);

    if (!esEmailValido(email)) {
      setResetResult({
        variant: 'danger',
        message: 'Ingresá un email válido.',
      });
      return;
    }

    setResetEmail(email);
    setResetResult(null);
    setResetLoading(true);

    try {
      const { error } = await supabase.auth.resetPasswordForEmail(email, {
        redirectTo: `${window.location.origin}/recuperar`,
      });

      if (error) throw error;

      setResetSent(true);
      setResetResult({
        variant: 'success',
        message: 'Correo enviado. Revisá tu bandeja de entrada y correo no deseado.',
      });
    } catch (error) {
      setResetResult({
        variant: 'danger',
        message: obtenerMensajeErrorAuth(
          error,
          'No fue posible enviar el correo de recuperación. Intentá nuevamente.'
        ),
      });
    } finally {
      setResetLoading(false);
    }
  };

  const cerrarModal = () => {
    if (resetLoading) return;

    setShowForgotPassword(false);
    setResetEmail('');
    setResetResult(null);
    setResetSent(false);
  };

  const abrirModalRecuperacion = () => {
    setResetEmail('');
    setResetResult(null);
    setResetSent(false);
    setShowForgotPassword(true);
  };

  return (
    <Container
      fluid
      className='login-page d-flex align-items-center justify-content-center'
    >
      <Row className='w-100 justify-content-center'>
        <Col xs={12} sm={10} md={8} lg={6} xl={5}>
          <Card className='login-card'>
            <Card.Body>
              <div className='login-header'>
                <div className='login-brand-mark' aria-hidden='true'>
                  <img src='/delyar_sa_logo.png' alt='' />
                </div>
                <div className='login-brand-copy'>
                  <span className='login-eyebrow'>Plataforma de gestión</span>
                  <h3>Servicios y Vencimientos</h3>
                  <p>Gestión centralizada de servicios, vencimientos y partidas</p>
                </div>
                <div className='login-title-divider' />
              </div>

              <Form onSubmit={handleLogin} className='login-form'>
                <FloatingLabel controlId='email' label='Email'>
                  <Form.Control
                    type='email'
                    name='email'
                    placeholder='Email'
                    autoComplete='email'
                    autoCapitalize='none'
                    spellCheck={false}
                    required
                    disabled={loading}
                  />
                </FloatingLabel>

                <FloatingLabel controlId='password' label='Contraseña'>
                  <Form.Control
                    type={mostrarPass ? 'text' : 'password'}
                    name='password'
                    placeholder='Contraseña'
                    autoComplete='current-password'
                    required
                    disabled={loading}
                  />
                </FloatingLabel>

                <Form.Check
                  type='checkbox'
                  label='Mostrar contraseña'
                  checked={mostrarPass}
                  onChange={(event) => setMostrarPass(event.target.checked)}
                  disabled={loading}
                />

                <Button
                  type='submit'
                  disabled={loading}
                  variant='primary'
                  className='login-submit'
                >
                  {loading ? (
                    <>
                      <Spinner animation='border' size='sm' className='me-2' />
                      Ingresando...
                    </>
                  ) : (
                    'Ingresar'
                  )}
                </Button>

                <Button
                  type='button'
                  variant='link'
                  className='login-forgot p-0'
                  onClick={abrirModalRecuperacion}
                  disabled={loading}
                >
                  ¿Olvidaste tu contraseña?
                </Button>

                <Alert
                  show={result.shown && !!result.message}
                  variant={result.variant}
                  dismissible
                  onClose={() => setResult((prev) => ({ ...prev, shown: false }))}
                  className='mt-3 mb-0'
                  role='alert'
                >
                  {result.message}
                </Alert>
              </Form>
            </Card.Body>
          </Card>
        </Col>
      </Row>

      <Modal
        show={showForgotPassword}
        onHide={cerrarModal}
        backdrop={resetLoading ? 'static' : true}
        keyboard={!resetLoading}
        centered
        className='recover-modal'
      >
        <Modal.Header closeButton={!resetLoading}>
          <Modal.Title>Recuperar contraseña</Modal.Title>
        </Modal.Header>

        <Form onSubmit={handleForgotPassword}>
          <Modal.Body>
            <Form.Group>
              <Form.Label>Email</Form.Label>

              <Form.Control
                type='email'
                value={resetEmail}
                onChange={(event) => {
                  setResetEmail(event.target.value);
                  if (resetResult) setResetResult(null);
                  if (resetSent) setResetSent(false);
                }}
                placeholder='Ingresá tu email'
                autoComplete='email'
                autoCapitalize='none'
                spellCheck={false}
                required
                disabled={resetLoading || resetSent}
                autoFocus
              />
            </Form.Group>

            <div className='recover-info'>
              Te enviaremos un correo con las instrucciones para reanudar el acceso a tu cuenta.
            </div>

            {resetResult && (
              <Alert
                variant={resetResult.variant}
                className='mt-3 mb-0'
                role='status'
                aria-live='polite'
              >
                {resetResult.message}
              </Alert>
            )}
          </Modal.Body>

          <Modal.Footer>
            <Button
              type='button'
              variant='secondary'
              onClick={cerrarModal}
              disabled={resetLoading}
            >
              Cerrar
            </Button>

            <Button
              type='submit'
              variant='primary'
              disabled={resetLoading || resetSent || !resetEmail.trim()}
            >
              {resetLoading ? (
                <>
                  <Spinner animation='border' size='sm' className='me-2' />
                  Enviando...
                </>
              ) : resetSent ? (
                'Enviado'
              ) : (
                'Enviar'
              )}
            </Button>
          </Modal.Footer>
        </Form>
      </Modal>
    </Container>
  );
}
